import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import { CommanderActionTypesEnum } from '@spazious/dollhouse-commander';
import { getShallowNodeValue, GraphUpdateAction } from '@spazious/dollhouse-shallow-graph';
import { useStaging } from '@spazious/dollhouse-store';
import { useEventCallback } from '@spazious/react-hooks';

import { useSelectedNodes } from '../../hooks/useSelectedNodes.js';
import { MetaFieldsContext } from '../../index.js';

import { DEFAULT_FIELD_PREFIX } from './InspectorFurniture.js';

export type MetaFieldsProviderProps = {
  children: React.ReactNode;
};

export const MetaFieldsProvider: React.FC<MetaFieldsProviderProps> = ({ children }) => {
  const [metaFieldValues, setMetaFieldValues] = useState<{ [key: string]: string }>({});

  const [globalFields, setGlobalFields] = useState<string[]>([]);
  const [editField, setEditField] = useState(-1);

  const actions = useRef<GraphUpdateAction[]>([]);

  const selectedNodes = useSelectedNodes();
  const { createAction, staging } = useStaging();

  const ignoredFields = useMemo(() => ['guest', 'menu', 'allergies', 'notes', 'numbering'], []);

  useEffect(() => {
    const customMetaFields = [] as string[];
    const metaFieldValues: { [key: string]: string } = {};

    if (selectedNodes.length === 1) {
      const nodeMetaFieldValues = getShallowNodeValue(selectedNodes[0], 'meta') ?? {};

      const updateCustomMetaFields = (field: string) => {
        if (!ignoredFields.includes(field)) {
          customMetaFields.push(field);
        }
      };
      const updateMetaFieldValues = (field: string) => {
        const defaultMetaField = ignoredFields.includes(field);

        // Filter out the field which is neither a custom field nor a default field
        if (!customMetaFields.includes(field) && !defaultMetaField) return;

        metaFieldValues[field] = nodeMetaFieldValues[field];
      };

      Object.keys(nodeMetaFieldValues).forEach((key) => {
        updateCustomMetaFields(key);
        updateMetaFieldValues(key);
      });
    }

    setGlobalFields(customMetaFields);
    setMetaFieldValues(metaFieldValues);
  }, [ignoredFields, selectedNodes]);

  const onAddField = useCallback(() => {
    selectedNodes.forEach(({ selector }) =>
      actions.current.push(
        createAction(
          'update',
          selector!,
          `meta.${DEFAULT_FIELD_PREFIX}${globalFields.length + 1}`,
          ''
        )
      )
    );
    setGlobalFields([...globalFields, `${DEFAULT_FIELD_PREFIX}${globalFields.length + 1}`]);
    setEditField(globalFields.length);
  }, [createAction, globalFields, selectedNodes]);

  const onSubmit = useEventCallback(() => {
    staging.add(actions.current);
    staging.commit(CommanderActionTypesEnum.Meta);
    actions.current = [];
  });

  const onRemoveField = useEventCallback((fieldName) => {
    selectedNodes.forEach(({ selector }) =>
      actions.current.push(createAction('update', selector!, `meta.${fieldName}`, undefined))
    );
    setGlobalFields(globalFields.filter((globalField) => globalField !== fieldName));
  });

  const onSubmitField = useEventCallback((evt: React.FocusEvent<HTMLInputElement>) => {
    if (evt.target.value !== '') {
      selectedNodes.forEach(({ selector }) => {
        actions.current.push(
          createAction(
            'update',
            selector!,
            `meta.${DEFAULT_FIELD_PREFIX}${editField + 1}`,
            undefined
          ),
          createAction('update', selector!, `meta.${evt.target.value}`, '')
        );
      });
    }
    const updatedGlobalFields = [
      ...globalFields.filter((field) => field !== `${DEFAULT_FIELD_PREFIX}${editField + 1}`),
      evt.target.value,
    ];
    setGlobalFields(updatedGlobalFields);
    setEditField(-1);
  });

  const onChangeField = useEventCallback((fieldName: string, value: string) => {
    selectedNodes.forEach(({ selector }) => {
      actions.current.push(createAction('update', selector!, `meta.${fieldName}`, value));
    });
    setMetaFieldValues({ ...metaFieldValues, [fieldName]: value });
  });

  const onEditField = useEventCallback((idx: number) => setEditField(idx));

  const value = {
    actions,
    ignoredFields,
    metaFieldValues,
    globalFields,
    editField,
    onAddField,
    onSubmit,
    onRemoveField,
    onSubmitField,
    onChangeField,
    onEditField,
  };
  return <MetaFieldsContext.Provider value={value}>{children}</MetaFieldsContext.Provider>;
};
